import React from 'react';
import { motion } from 'framer-motion';
import { Plus, MessageCircle, Settings, LogOut, Users, X, Clock } from 'lucide-react';
import { AdvisorAvatar } from './AdvisorAvatar';
import { User } from '@supabase/supabase-js';
import { Database } from '../lib/supabase';

type UserProfile = Database['public']['Tables']['user_profiles']['Row'];

interface Chat {
  id: string;
  title: string;
  participants: string[];
  lastMessage: string;
  timestamp: string;
  isGroup: boolean;
}

interface SidebarProps {
  user: User | null;
  profile: UserProfile | null;
  showMobileSidebar: boolean;
  setShowMobileSidebar: (show: boolean) => void;
  onNewChatClick: () => void;
  chats: Chat[];
  onChatClick: (chatId: string, isGroup: boolean) => void;
  onSignOut: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  user,
  profile,
  showMobileSidebar,
  setShowMobileSidebar,
  onNewChatClick,
  chats,
  onChatClick,
  onSignOut
}) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const displayName = profile?.preferred_name || user?.user_metadata?.full_name || 'User';
  const displayEmail = user?.email || 'user@example.com';

  return (
    <>
      {/* Sidebar */}
      <div className={`${
        showMobileSidebar ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-40 w-80 bg-black/40 backdrop-blur-sm border-r border-white/10 flex flex-col transition-transform duration-300 ease-in-out`}>
        
        {/* Header - Hidden on mobile */}
        <div className="hidden lg:block p-6 border-b border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-400 to-purple-500 flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Boardroom</h1>
              <p className="text-white/60 text-sm">Your Advisory Hub</p>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onNewChatClick}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-orange-500 to-purple-500 text-white font-semibold hover:from-orange-400 hover:to-purple-400 transition-all duration-300"
          >
            <Plus className="w-5 h-5" />
            New Chat
          </motion.button>
        </div>

        {/* Mobile New Chat Button */}
        <div className="lg:hidden p-4 pt-20 border-b border-white/10">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onNewChatClick}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-gradient-to-r from-orange-500 to-purple-500 text-white font-semibold hover:from-orange-400 hover:to-purple-400 transition-all duration-300"
          >
            <Plus className="w-5 h-5" />
            New Chat
          </motion.button>
        </div>

        {/* Chat History */}
        <div className="flex-1 p-6 overflow-y-auto">
          <h3 className="text-white font-semibold mb-4">Recent Conversations</h3>
          <div className="space-y-3">
            {chats.map((chat) => (
              <motion.div
                key={chat.id}
                whileHover={{ scale: 1.02 }}
                onClick={() => {
                  onChatClick(chat.id, chat.isGroup);
                  setShowMobileSidebar(false);
                }}
                className="p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 cursor-pointer transition-all duration-300"
              >
                <div className="flex items-start gap-3">
                  <MessageCircle className="w-5 h-5 text-orange-400 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-medium truncate mb-1">{chat.title}</h4>
                    <div className="flex items-center gap-2 mb-2">
                      {chat.participants.slice(0, 3).map((participant, index) => (
                        <AdvisorAvatar key={participant} name={participant} size="small" />
                      ))}
                      {chat.participants.length > 3 && (
                        <span className="text-white/60 text-xs">+{chat.participants.length - 3}</span>
                      )}
                      <span className="text-white/40 text-xs">
                        {chat.isGroup ? 'Group' : '1:1'}
                      </span>
                    </div>
                    <p className="text-white/60 text-sm truncate mb-1">{chat.lastMessage}</p>
                    <p className="text-white/40 text-xs">{chat.timestamp}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* User Profile & Settings */}
        <div className="p-6 border-t border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-400 to-blue-500 flex items-center justify-center">
                <span className="text-white font-bold">{getInitials(displayName)}</span>
              </div>
              <div>
                <p className="text-white font-medium">{displayName}</p>
                <p className="text-white/60 text-sm truncate max-w-[140px]" title={displayEmail}>{displayEmail}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all duration-300">
                <Settings className="w-5 h-5" />
              </button>
              <button 
                onClick={onSignOut}
                className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all duration-300"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {showMobileSidebar && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setShowMobileSidebar(false)}
        />
      )}
    </>
  );
};